import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { Ticket, ShoppingCart } from 'lucide-react';

/**
 * AdCreditsWidget — shows the current user's ad-credit balance + upsell CTA.
 * Used on the dashboard sidebar / overview.
 */
export const AdCreditsWidget = ({ className = '' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get('/ad-credits/mine')
      .then(r => { if (alive) setData(r.data); })
      .catch(() => setData(null))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  const remaining = data?.credits_remaining || 0;

  return (
    <Card className={`p-4 ${className}`} data-testid='ad-credits-widget'>
      <div className='flex items-center gap-2 mb-2'>
        <Ticket className='h-4 w-4 text-primary' />
        <div className='text-sm font-semibold'>Ad credits</div>
      </div>
      {remaining > 0 ? (
        <>
          <div className='text-3xl font-semibold tabular-nums' data-testid='ad-credits-remaining'>{remaining}</div>
          <div className='text-xs text-muted-foreground mt-0.5'>credit(s) remaining</div>
          <div className='text-[11px] text-muted-foreground mt-2'>
            Each credit posts an ad with 4 images + 1 video + auto-Featured for {data?.next_credit?.featured_days_per_ad || 2} day(s).
          </div>
          <Button asChild size='sm' variant='outline' className='w-full mt-3'>
            <Link to='/dashboard/listings/new' data-testid='ad-credits-use-cta'>Use a credit — post ad</Link>
          </Button>
        </>
      ) : (
        <>
          <div className='text-sm text-muted-foreground mt-1'>
            No credits yet. Buy an ad pack to try IndusFact without a subscription.
          </div>
          <Button asChild size='sm' className='w-full mt-3'>
            <Link to='/pricing#pricing-adpacks' data-testid='ad-credits-buy-cta'>
              <ShoppingCart className='h-3.5 w-3.5 mr-1' /> Buy an ad pack
            </Link>
          </Button>
        </>
      )}
      {data && data.lifetime_purchased > 0 && (
        <div className='text-[10px] text-muted-foreground mt-2 pt-2 border-t'>
          Lifetime credits purchased: {data.lifetime_purchased}
        </div>
      )}
    </Card>
  );
};

export default AdCreditsWidget;
